import { Router } from '@reach/router';
import { TFunction } from 'i18next';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import { Provider } from 'overmind-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LoginState } from '../interfaces/internal';
import css from './App.module.scss';
import { AutomationSupportWidget } from './components/AutomationSupportWidget/AutomationSupportWidget';
import { ContactWidget } from './components/ContactWidget/ContactWidget';
import { KalmarInsightWidget } from './components/KalmarInsightWidget/KalmarInsightWidget';
import { MainContainer } from './components/MainContainer/MainContainer';
import { MaintenanceReportingWidget } from './components/MaintenanceReportingWidget/MaintenanceReportingWidget';
import { MaintenanceScheduleWidget } from './components/MaintenanceScheduleWidget/MaintenanceScheduleWidget';
import { MyCasesWidget } from './components/MyCasesWidget/MyCasesWidget';
import { MyEquipmentWidget } from './components/MyEquipmentWidget/MyEquipmentWidget';
import { MyPartsWidget } from './components/MyPartsWidget/MyPartsWidget';
import { StatusPage } from './components/StatusPage/StatusPage';
import { WidgetProps } from './components/Widget/Widget';
import { WidgetGrid } from './components/WidgetGrid/WidgetGrid';
import { WidgetRouter } from './components/WidgetRouter/WidgetRouter';
import { MascusWidget } from './components/MascusWidget/MascusWidget';
import { store, useActions, useOvermindState } from './state';
import { State } from './state/state';
import {
    getCaseManagementLink,
    getDealerCommunityLink,
    getInsightLink,
    getInsightRegisterLink,
    getMascusLink,
    getStoreRegisterLink,
    getTechsightLink,
    mkEnv,
} from './utils';
import { DealerCommunityWidget } from './components/DealerCommunityWidget/DealerCommunityWidget';
import { DocumentationPageProWidget } from './components/DocumentationPageProWidget/DocumentationPageProWidget';
import { ServiceRequestWidget } from './components/ServiceRequestWidget/ServiceRequestWidget';
import { TrainingRequestWidget } from './components/TrainingRequestWidget/TrainingRequestWidget';
import { EmptyWidget } from './components/EmptyWidget/EmptyWidget';
import { TechsightWidget } from './components/TechsightWidget/TechsightWidget';
import { StockEquipmentWidget } from './components/StockEquipmentWidget/StockEquipmentWidget';
import { ButtonVariant } from './components/Button/Button';

const getMyCasesButtons = (state: State, t: TFunction) => {
    const { userAccess, cases } = state;
    const showCasesButton =  {
        variant: 'secondary' as ButtonVariant,
        text: t('Show all cases'),
        linkTo: getCaseManagementLink('/s/recordlist/Case/Default'),
        id: 'show-cases-button',
    };
    const createNewCaseButton = {
        variant: 'primary' as ButtonVariant,
        text: `+ ${t('Create new')}`,
        linkTo: getCaseManagementLink('/s/create-case'),
        id: 'create-new-case-button',
    };

    const isStoreOnlyUser = userAccess.mascusWidget.read && !userAccess.automationSupport.read && !userAccess.kalmarInsight.read;

    // Purely store users can't create store cases yet
    if(userAccess.cases.create && !isStoreOnlyUser) {
        return cases.length > 0 ? [createNewCaseButton, showCasesButton] : [createNewCaseButton];
    }

    return [showCasesButton];
}

const GetWidgets = (state: State, t: TFunction): WidgetProps[] => {
    const {
        canNavigateToMyParts,
        contact,
        loadingStates,
        siteEquipment,
        user,
        userAccess,
        equipment,
        selectedSite,
        sites,
        selectedCustomer,
        salesOrders,
        insightLicenseCounts,
        insightUserCount,
    } = state;

    const actions = useActions();
    const insightLoginPath = user?.isAdmin ? 'internal' : 'login/salesforce';

    const shouldShowInsightWidget =
        userAccess.kalmarInsight.read &&
        ((insightUserCount ?? 0) > 0 ||
            insightLicenseCounts?.expired > 0 ||
            insightLicenseCounts?.valid > 0 ||
            insightLicenseCounts?.expiring > 0);
    const shouldShowContactsWidget = userAccess.contacts.read && !isNil(contact);
    const isMyPartsWidgetFirst = user?.myParts.active ?? false;
    const shouldShowEquipmentWidget = equipment.length > 0 || loadingStates.equipment !== 'successful';
    const shouldShowDealerCommunityWidget = userAccess.dealerCommunity.read;
    const shouldShowMascusWidget = userAccess.mascusWidget.read;
    const shouldShowTrainingRequestWidget = userAccess.trainingRequest.read;
    const shouldShowServiceRequestWidget = userAccess.serviceRequest.read;
    const shouldShowTechsightWidget = userAccess.techsight.read;

    const myPartsWidget: WidgetProps | undefined =
        userAccess.salesOrders.read
            ? {
                  id: salesOrders.length > 0 ? 'my-parts' : 'empty-my-parts',
                  title: t('Parts order history'),
                  content: salesOrders.length > 0 ? <MyPartsWidget /> : <EmptyWidget type="parts" />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Open MyKalmar STORE'),
                          linkTo: canNavigateToMyParts ? `${mkEnv.myPartsURL}/orders` : `${mkEnv.myPartsInternalURL}`,
                          id: 'open-my-parts-button',
                      },
                  ],
                  loadingState: loadingStates.salesOrders,
                  failureProps: {
                      translationKey: 'Loading sales orders failed',
                      retry: actions.getSalesOrders,
                  },
              }
            : {
                  id: 'empty-my-parts',
                  title: t('Parts order history'),
                  content: <EmptyWidget type="parts" />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Read more and register'),
                          // TODO: URL
                          linkTo: getStoreRegisterLink(),
                          id: 'register-my-parts-button',
                          openInNewTab: true,
                      },
                  ],
                  loadingState: loadingStates.salesOrders,
                  failureProps: {
                      translationKey: 'Loading sales orders failed',
                      retry: actions.getSalesOrders,
                  },
              };

    return compact([
        isMyPartsWidgetFirst && myPartsWidget,
        userAccess.equipment.read && shouldShowEquipmentWidget
            ? {
                  id: 'my-equipment',
                  title: t('My Equipment'),
                  content: <MyEquipmentWidget />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Show all ({{ equipmentLength }}) equipment', {
                              equipmentLength: siteEquipment.length,
                          }),
                          linkTo: '/my-equipment',
                          id: 'show-all-equipment-button',
                      },
                  ],
                  loadingState: loadingStates.equipment,
                  failureProps: {
                      translationKey: 'Loading equipment failed',
                      retry: actions.getEquipment,
                  },
              }
            : {
                  id: 'my-equipment',
                  title: t('My Equipment'),
                  content: <EmptyWidget type="equipment" />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Register Kalmar equipment'),
                          linkTo: '/my-equipment/register',
                          id: 'register-equipment-button',
                      },
                  ],
              },
        userAccess.cases.read
            ? {
                  id: 'my-cases',
                  title: t('My Cases'),
                  content: <MyCasesWidget />,
                  footerButtons: compact(getMyCasesButtons(state, t)),
                  loadingState: loadingStates.cases,
                  failureProps: {
                      translationKey: 'Loading cases failed',
                      retry: actions.getCases,
                  },
              }
            : undefined,
        !isMyPartsWidgetFirst && myPartsWidget,
        userAccess.maintenancePlans.read
            ? {
                  id: 'maintenance-schedule',
                  title: t('Maintenance schedule'),
                  content: <MaintenanceScheduleWidget />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Show on calendar'),
                          linkTo: '/maintenance-schedule',
                          id: 'show-maintenance-calendar-button',
                      },
                  ],
                  loadingState: loadingStates.maintenancePlans,
                  failureProps: {
                      translationKey: 'Loading maintenance schedule failed',
                      retry: actions.getInitialMaintenancePlans,
                  },
              }
            : undefined,
        userAccess.maintenanceReporting.read
            ? {
                  id: 'maintenance-reporting',
                  title: t('Maintenance reporting'),
                  content: <MaintenanceReportingWidget />,
              }
            : undefined,
        shouldShowInsightWidget
            ? {
                  id: 'kalmar-insight',
                  title: 'MyKalmar INSIGHT',
                  content: <KalmarInsightWidget />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Open MyKalmar INSIGHT'),
                          linkTo: getInsightLink(insightLoginPath),
                          id: 'open-kalmar-insight-button',
                      },
                  ],
              }
            : {
                  id: 'empty-insight',
                  title: t('MyKalmar INSIGHT'),
                  content: <EmptyWidget type="insight" />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Read more and subscribe'),
                          linkTo: getInsightRegisterLink(),
                          id: 'register-insight-button',
                          openInNewTab: true,
                      },
                  ],
              },
        userAccess.stockEquipment.read
            ? {
                  id: 'stock-equipment',
                  title: t('Stock equipment'),
                  content: <StockEquipmentWidget />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Browse stock equipment'),
                          linkTo: 'stock-equipment',
                          id: 'show-equipment-stock-button',
                      },
                  ],
              }
            : undefined,
        shouldShowDealerCommunityWidget
            ? {
                  id: 'dealer-community',
                  title: t('Dealer community'),
                  content: <DealerCommunityWidget />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Open Dealer community'),
                          linkTo: getDealerCommunityLink(),
                          id: 'open-dealer-community-button',
                          openInNewTab: true,
                      },
                  ],
              }
            : undefined,
        shouldShowServiceRequestWidget
            ? {
                  id: 'service-requests',
                  title: t('Kalmar Care service request'),
                  content: <ServiceRequestWidget />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Show requests'),
                          linkTo: '/service-requests',
                          id: 'open-service-requests-button',
                      },
                      {
                          variant: 'secondary',
                          text: `+ ${t('Request service')}`,
                          linkTo: '/service-requests/form',
                          id: 'open-fill-service-requests-button',
                      },
                  ],
              }
            : undefined,
        shouldShowTrainingRequestWidget
            ? {
                  id: 'training-requests',
                  title: t('Kalmar Training Academy'),
                  content: <TrainingRequestWidget />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Show requests'),
                          linkTo: '/training-requests',
                          id: 'open-training-requests-button',
                      },
                      {
                          variant: 'secondary',
                          text: `+ ${t('Request training')}`,
                          linkTo: '/training-requests/form',
                          id: 'open-fill-training-requests-button',
                      },
                  ],
              }
            : undefined,
        shouldShowMascusWidget
            ? {
                  id: 'mascus',
                  title: t('Kalmar Used Equipment'),
                  content: <MascusWidget />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Open used equipment sales portal'),
                          linkTo: getMascusLink(selectedSite, sites, selectedCustomer),
                          id: 'open-mascus-button',
                          openInNewTab: true,
                      },
                  ],
              }
            : undefined,
        userAccess.documentationPagePro.read
            ? {
                  id: 'documentation-page-pro',
                  title: t('Manuals'),
                  content: <DocumentationPageProWidget />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Show manuals search'),
                          linkTo: '/documentation-page-pro',
                          id: 'search-manuals-button',
                      },
                  ],
              }
            : undefined,
        shouldShowTechsightWidget
            ? {
                  id: 'techsight',
                  title: t('TechSight'),
                  content: <TechsightWidget />,
                  footerButtons: [
                      {
                          variant: 'secondary',
                          text: t('Open TechSight'),
                          linkTo: getTechsightLink(),
                          id: 'open-techsight-button',
                          openInNewTab: true,
                      },
                  ],
              }
            : undefined,
        shouldShowContactsWidget
            ? {
                  id: 'contacts',
                  title: t('Contact Kalmar'),
                  content: <ContactWidget />,
              }
            : undefined,
        userAccess.automationSupport.read
            ? {
                  id: 'automation-support',
                  title: t('Automation support'),
                  content: <AutomationSupportWidget />,
              }
            : undefined,
    ]);
};

const AppContent: React.FC = () => {
    const state = useOvermindState();
    const { loginState } = state;

    const { handleLogin } = useActions();

    const { t } = useTranslation();
    const widgets = GetWidgets(state, t);

    if (loginState === LoginState.LoggedOut) {
        return (
            <StatusPage
                buttonProps={{
                    onClick: () => handleLogin(),
                    text: t('Login again'),
                    variant: 'secondary',
                }}
                isLoading={false}
                title={t('You are now logged out from MyKalmar')}
            />
        );
    }

    if (loginState === LoginState.NotStarted) {
        return <StatusPage isLoading={true} title={`${t('Loading MyKalmar')}...`} />;
    }

    if (loginState === LoginState.Successful) {
        return (
            <Router className={css.router}>
                <MainContainer path="/" default>
                    <WidgetGrid path="/" widgets={widgets} />
                    <WidgetRouter default widgets={widgets} />
                </MainContainer>
            </Router>
        );
    }

    return (
        <StatusPage
            buttonProps={{
                onClick: () => window.location.reload(),
                text: t('Try again'),
                variant: 'secondary',
            }}
            isLoading={false}
            title={t('Something went wrong')}
        />
    );
};

const App: React.FC = () => {
    return (
        <Provider value={store}>
            <AppContent key={store.state.language} />
        </Provider>
    );
};

export default App;
